/**
 * DataValidationService - Comprehensive data validation and quality assurance
 * 
 * This service provides validation for all data types used in the application,
 * ensuring data integrity and preventing rendering issues.
 */

import { log } from './LoggingService';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  metadata?: {
    dataSize?: number;
    validationTime?: number;
    source?: string;
  };
}

export interface ChartDataValidation {
  hasData: boolean;
  hasValidStructure: boolean;
  hasValidValues: boolean;
  dataPoints: number;
  errors: string[];
  warnings: string[];
}

export class DataValidationService {
  private static instance: DataValidationService;

  private constructor() {
    // Private constructor for singleton pattern
  }

  public static getInstance(): DataValidationService {
    if (!DataValidationService.instance) {
      DataValidationService.instance = new DataValidationService();
    }
    return DataValidationService.instance;
  }

  /**
   * Validate V2ChartData structure and content
   */
  public validateChartData(data: any): ChartDataValidation {
    const startTime = Date.now();
    const errors: string[] = [];
    const warnings: string[] = [];
    let dataPoints = 0;

    // Check if data exists
    if (!data) {
      errors.push('Chart data is null or undefined');
      return {
        hasData: false,
        hasValidStructure: false,
        hasValidValues: false,
        dataPoints: 0,
        errors,
        warnings
      };
    }

    // Check basic structure
    if (!data.chart_type) {
      errors.push('Missing chart_type property');
    }

    if (!data.title) {
      warnings.push('Missing title property');
    }

    if (!data.x_axis || !Array.isArray(data.x_axis)) {
      errors.push('Missing or invalid x_axis property');
    } else {
      dataPoints = data.x_axis.length;
    }

    if (!data.series || !Array.isArray(data.series)) {
      errors.push('Missing or invalid series property');
    } else {
      // Validate each series
      data.series.forEach((series: any, index: number) => {
        if (!series.name) {
          errors.push(`Series ${index} missing name property`);
        }
        if (!series.data || !Array.isArray(series.data)) {
          errors.push(`Series ${index} missing or invalid data property`);
        } else if (series.data.length !== dataPoints) {
          errors.push(`Series ${index} data length (${series.data.length}) doesn't match x_axis length (${dataPoints})`);
        }
        if (!series.color) {
          warnings.push(`Series ${index} missing color property`);
        }
      });
    }

    // Validate data values
    const hasValidValues = this.validateDataValues(data, errors, warnings);

    const validationTime = Date.now() - startTime;

    log.debug(`Data validation completed in ${validationTime}ms`, 'DataValidation', {
      dataPoints,
      errors: errors.length,
      warnings: warnings.length
    });

    return {
      hasData: true,
      hasValidStructure: errors.length === 0,
      hasValidValues,
      dataPoints,
      errors,
      warnings
    };
  }

  /**
   * Validate data values for consistency and reasonableness
   */
  private validateDataValues(data: any, errors: string[], warnings: string[]): boolean {
    let isValid = true;

    if (data.series && Array.isArray(data.series)) {
      data.series.forEach((series: any, seriesIndex: number) => {
        if (series.data && Array.isArray(series.data)) {
          series.data.forEach((value: any, dataIndex: number) => {
            // Check for NaN values
            if (typeof value === 'number' && isNaN(value)) {
              errors.push(`Series ${seriesIndex}, data point ${dataIndex} is NaN`);
              isValid = false;
            }
            // Check for negative values in funding data
            else if (typeof value === 'number' && value < 0) {
              warnings.push(`Series ${seriesIndex}, data point ${dataIndex} has negative value: ${value}`);
            }
            // Check for extremely large values
            else if (typeof value === 'number' && value > 1e12) {
              warnings.push(`Series ${seriesIndex}, data point ${dataIndex} has extremely large value: ${value}`);
            }
          });
        }
      });
    }

    return isValid;
  }

  /**
   * Validate country funding data
   */
  public validateCountryData(data: any): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    const startTime = Date.now();

    if (!data) {
      errors.push('Country data is null or undefined');
      return { isValid: false, errors, warnings };
    }

    if (!data.countries || !Array.isArray(data.countries)) {
      errors.push('Missing or invalid countries array');
      return { isValid: false, errors, warnings };
    }

    data.countries.forEach((country: any, index: number) => {
      if (!country.country_name) {
        errors.push(`Country ${index} missing country_name`);
      }
      if (!country.country_code) {
        warnings.push(`Country ${index} missing country_code`);
      }
      if (typeof country.funding_amount !== 'number') {
        errors.push(`Country ${index} has invalid funding_amount: ${country.funding_amount}`);
      }
      if (typeof country.deal_count !== 'number') {
        errors.push(`Country ${index} has invalid deal_count: ${country.deal_count}`);
      }
    });

    const validationTime = Date.now() - startTime;

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      metadata: {
        dataSize: data.countries.length,
        validationTime,
        source: 'country_validation'
      }
    };
  }

  /**
   * Validate category data
   */
  public validateCategoryData(data: any): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    const startTime = Date.now();

    if (!data) {
      errors.push('Category data is null or undefined');
      return { isValid: false, errors, warnings };
    }

    if (!data.categories || !Array.isArray(data.categories)) {
      errors.push('Missing or invalid categories array');
      return { isValid: false, errors, warnings };
    }

    data.categories.forEach((category: any, index: number) => {
      if (!category.name) {
        errors.push(`Category ${index} missing name`);
      }
      if (typeof category.amount !== 'number' && typeof category.funding_amount !== 'number') {
        errors.push(`Category ${index} has invalid amount: ${category.amount || category.funding_amount}`);
      }
      if (typeof category.deal_count !== 'number') {
        warnings.push(`Category ${index} has invalid deal_count: ${category.deal_count}`);
      }
    });

    const validationTime = Date.now() - startTime;

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      metadata: {
        dataSize: data.categories.length,
        validationTime,
        source: 'category_validation'
      }
    };
  }

  /**
   * Validate API response structure
   */
  public validateApiResponse(response: any): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    const startTime = Date.now();

    if (!response) {
      errors.push('API response is null or undefined');
      return { isValid: false, errors, warnings };
    }

    if (!response.charts) {
      errors.push('Missing charts property in API response');
    } else {
      // Validate each chart
      Object.keys(response.charts).forEach(chartKey => {
        const chartData = response.charts[chartKey];
        const chartValidation = this.validateChartData(chartData);
        
        if (!chartValidation.hasValidStructure) {
          errors.push(`Chart ${chartKey} has structural issues: ${chartValidation.errors.join(', ')}`);
        }
        
        if (chartValidation.warnings.length > 0) {
          warnings.push(`Chart ${chartKey} warnings: ${chartValidation.warnings.join(', ')}`);
        }
      });
    }

    if (!response.metadata) {
      warnings.push('Missing metadata property in API response');
    }

    const validationTime = Date.now() - startTime;

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      metadata: {
        validationTime,
        source: 'api_response_validation'
      }
    };
  }

  /**
   * Validate year visibility state
   */
  public validateYearVisibility(yearVisibility: any): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!yearVisibility) {
      errors.push('Year visibility state is null or undefined');
      return { isValid: false, errors, warnings };
    }

    const requiredYears = ['show2023', 'show2024', 'show2025'];
    requiredYears.forEach(year => {
      if (typeof yearVisibility[year] !== 'boolean') {
        errors.push(`Year visibility ${year} is not a boolean: ${yearVisibility[year]}`);
      }
    });

    // Check if at least one year is visible
    const visibleYears = requiredYears.filter(year => yearVisibility[year]);
    if (visibleYears.length === 0) {
      warnings.push('No years are visible in year visibility state');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      metadata: {
        source: 'year_visibility_validation'
      }
    };
  }

  /**
   * Get validation summary for debugging
   */
  public getValidationSummary(validations: ValidationResult[]): {
    totalValidations: number;
    validCount: number;
    errorCount: number;
    warningCount: number;
    totalErrors: number;
    totalWarnings: number;
  } {
    const totalValidations = validations.length;
    const validCount = validations.filter(v => v.isValid).length;
    const errorCount = validations.filter(v => v.errors.length > 0).length;
    const warningCount = validations.filter(v => v.warnings.length > 0).length;
    const totalErrors = validations.reduce((sum, v) => sum + v.errors.length, 0);
    const totalWarnings = validations.reduce((sum, v) => sum + v.warnings.length, 0);

    return {
      totalValidations,
      validCount,
      errorCount,
      warningCount,
      totalErrors,
      totalWarnings
    };
  }
}

// Export singleton instance
export const dataValidationService = DataValidationService.getInstance();